import React, { useState, useEffect, useRef, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Loader2, Search, X } from "lucide-react";
import { interfaceRepo } from "@/lib/api-services";
import { cn } from "@/lib/utils";

export interface SearchDropdownProps {
  type: "All" | "Internal" | "External";
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchDropdown({
  type,
  value = "",
  onChange,
  placeholder,
  disabled = false,
  className,
}: SearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [items, setItems] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchIdRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Is a search actively underway (debouncing or API request in-flight)
  const isSearching = searchQuery.trim() !== debouncedQuery || isLoading;

  // Fetch items function
  const fetchPage = useCallback(
    async (pageToFetch: number, query: string, append: boolean = false) => {
      const currentFetchId = ++fetchIdRef.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const res = await interfaceRepo.searchInterfaces({
          type,
          name: query || undefined,
          page: pageToFetch,
          pageSize: 20,
        });

        if (currentFetchId !== fetchIdRef.current) return;

        if (append) {
          setItems((prev) => Array.from(new Set([...prev, ...res.items])));
        } else {
          setItems(Array.from(new Set(res.items)));
        }

        setHasMore(res.hasMore);
        setPage(pageToFetch);
      } catch (err) {
        console.error(`Error searching interfaces (${type}):`, err);
        if (currentFetchId === fetchIdRef.current) {
          if (!append) setItems([]);
          setHasMore(false);
        }
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [type]
  );

  // Pre-fetch initial data on page load (component mount)
  useEffect(() => {
    fetchPage(1, "", false);
  }, [fetchPage]);

  // Refetch when debounced search query changes
  useEffect(() => {
    fetchPage(1, debouncedQuery, false);
  }, [debouncedQuery, fetchPage]);

  // If value is reset externally (e.g. Refresh button), clear internal search query if needed
  useEffect(() => {
    if (!value && searchQuery) {
      setSearchQuery("");
      setDebouncedQuery("");
    }
  }, [value]);

  // Infinite scroll on reach bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      if (!isLoading && !isLoadingMore && !isSearching && hasMore) {
        fetchPage(page + 1, debouncedQuery, true);
      }
    }
  };

  const handleSelect = (selectedName: string) => {
    onChange(selectedName);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const typeDisplayLabel =
    type === "All"
      ? "Interfaces"
      : type === "Internal"
      ? "Internal Interfaces"
      : "External Interfaces";

  const defaultPlaceholder =
    placeholder ||
    (type === "All"
      ? "Select Interface Name"
      : type === "Internal"
      ? "Select Internal Interface Name"
      : "Select External Interface Name");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal text-left h-9 px-3 bg-white hover:bg-slate-50 transition-colors",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate flex items-center gap-2">
            {isLoading && items.length === 0 ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500 shrink-0" />
                <span className="text-muted-foreground">Loading interfaces...</span>
              </>
            ) : (
              value || defaultPlaceholder
            )}
          </span>
          <div className="flex items-center shrink-0 ml-2 space-x-1">
            {value && (
              <span
                role="button"
                tabIndex={0}
                className="p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleClear}
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0 shadow-lg border rounded-md bg-white z-50"
        align="start"
      >
        {/* Search input with debounced query & searching indicator */}
        <div className="flex items-center border-b px-3 py-2 gap-2 bg-slate-50/50">
          {isSearching ? (
            <Loader2 className="h-4 w-4 shrink-0 text-workfinity-500 animate-spin" />
          ) : (
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <input
            className="flex h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder="Search interface..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="text-muted-foreground hover:text-foreground p-0.5 transition-colors"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Scrollable list with infinite scroll */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-60 overflow-y-auto p-1 divide-y divide-border/20"
        >
          {/* Active Search in Progress */}
          {isSearching ? (
            <div className="flex flex-col items-center justify-center py-6 text-sm text-muted-foreground space-y-2">
              <Loader2 className="h-5 w-5 animate-spin text-workfinity-500" />
              <span className="font-medium text-slate-700">
                {searchQuery.trim()
                  ? `Searching for "${searchQuery.trim()}"...`
                  : "Loading interfaces..."}
              </span>
            </div>
          ) : (
            <>
              {/* Option for All / Empty filter */}
              <div
                onClick={() => handleSelect("")}
                className={cn(
                  "flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer hover:bg-slate-100 transition-colors",
                  !value && "font-medium text-workfinity-600 bg-workfinity-50/60"
                )}
              >
                <span>All {typeDisplayLabel}</span>
                {!value && <Check className="h-4 w-4 text-workfinity-600 shrink-0" />}
              </div>

              {/* If current value is not in items, show it selected */}
              {value && !items.includes(value) && (
                <div
                  onClick={() => handleSelect(value)}
                  className="flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer bg-workfinity-50/60 font-medium text-workfinity-600"
                >
                  <span className="truncate">{value}</span>
                  <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-2" />
                </div>
              )}

              {/* Items */}
              {items.map((item, idx) => (
                <div
                  key={`${item}-${idx}`}
                  onClick={() => handleSelect(item)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer hover:bg-slate-100 transition-colors",
                    value === item && "font-medium text-workfinity-600 bg-workfinity-50/60"
                  )}
                >
                  <span className="truncate">{item}</span>
                  {value === item && <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-2" />}
                </div>
              ))}

              {/* Empty state */}
              {items.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {searchQuery.trim()
                    ? `No interfaces found matching "${searchQuery.trim()}"`
                    : "No interfaces found"}
                </div>
              )}

              {/* Scroll loading indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-2 text-xs text-muted-foreground space-x-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500" />
                  <span>Loading more...</span>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
